import React, { useState, useRef, useCallback } from 'react';
import {
  BIKES,
  UPGRADES,
  CONDITION_OPTIONS,
  estimateBikeValue,
  fmt,
} from '../../../../util/emotoValuation';
import css from './SectionValueCalculator.module.css';

const BADGE_CLASS_MAP = {
  'badge-hot': 'badgeHot',
  'badge-stable': 'badgeStable',
  'badge-soft': 'badgeSoft',
  'badge-disc': 'badgeDisc',
};

/**
 * Used emoto value calculator.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.sectionId] section anchor id, so that in-page links (e.g. the
 *   hero's "free value estimate" button) can scroll to this section
 * @returns {JSX.Element}
 */
const SectionValueCalculator = props => {
  const { sectionId } = props;
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [mileage, setMileage] = useState('');
  const [batteryCycles, setBatteryCycles] = useState('');
  const [crash, setCrash] = useState('0');
  const [condVal, setCondVal] = useState(null);
  const [activeUpg, setActiveUpg] = useState(new Set());
  const [result, setResult] = useState(null);
  const resultsRef = useRef(null);

  const models = make && BIKES[make] ? Object.keys(BIKES[make].models) : [];
  const years =
    make && model && BIKES[make]?.models[model]
      ? BIKES[make].models[model].years.slice().reverse()
      : [];

  const handleMakeChange = useCallback(e => {
    setMake(e.target.value);
    setModel('');
    setYear('');
  }, []);

  const handleModelChange = useCallback(e => {
    setModel(e.target.value);
    setYear('');
  }, []);

  const toggleUpgrade = useCallback(id => {
    setActiveUpg(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const calculate = useCallback(() => {
    if (!make || !model || !year) {
      alert('Please select make, model, and year.');
      return;
    }
    if (condVal === null) {
      alert('Please select a condition grade.');
      return;
    }

    const res = estimateBikeValue({
      make,
      model,
      year,
      condition: condVal,
      mileage,
      batteryCycles,
      crash,
      upgrades: Array.from(activeUpg),
    });

    if (res) {
      setResult(res);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  }, [make, model, year, mileage, batteryCycles, crash, condVal, activeUpg]);

  return (
    <section className={css.root} id={sectionId}>
      <div className={css.wrap}>
        <div className={css.headerBlock}>
          <div className={css.headerEyebrow}>Light Electric Motorcycle Market</div>
          {/* h2, not h1: the hero above owns the page's single h1. */}
          <h2 className={css.heading}>
            Used emoto
            <br />
            <span className={css.headingAccent}>value calculator</span>
          </h2>
          <p className={css.headerDesc}>
            Get an estimated private-party resale value for your electric dirt bike or e-moto based
            on make, model, year, condition, and installed upgrades — updated with 2025 market data.
          </p>
        </div>

        {/* Bike Details */}
        <div className={css.formBlock}>
          <div className={css.formBlockTitle}>
            <span className={css.dot} /> Bike Details
          </div>
          <div className={css.fgrid3} style={{ marginBottom: 16 }}>
            <div className={css.fg}>
              <span className={css.label}>Make</span>
              <select className={css.select} value={make} onChange={handleMakeChange}>
                <option value="">Select make</option>
                {Object.entries(BIKES).map(([key, b]) => (
                  <option key={key} value={key}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={css.fg}>
              <span className={css.label}>Model</span>
              <select className={css.select} value={model} onChange={handleModelChange}>
                <option value="">{make ? 'Select model' : '— select make first —'}</option>
                {models.map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className={css.fg}>
              <span className={css.label}>Year</span>
              <select className={css.select} value={year} onChange={e => setYear(e.target.value)}>
                <option value="">{model ? 'Select year' : '—'}</option>
                {years.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={css.fgrid3}>
            <div className={css.fg}>
              <span className={css.label}>Approx. Mileage (optional)</span>
              <input
                className={css.inputNumber}
                type="number"
                placeholder="Miles ridden"
                min="0"
                max="20000"
                value={mileage}
                onChange={e => setMileage(e.target.value)}
              />
            </div>
            <div className={css.fg}>
              <span className={css.label}>Battery Charge Cycles (optional)</span>
              <input
                className={css.inputNumber}
                type="number"
                placeholder="Cycles (e.g. 75)"
                min="0"
                max="3000"
                value={batteryCycles}
                onChange={e => setBatteryCycles(e.target.value)}
              />
            </div>
            <div className={css.fg}>
              <span className={css.label}>Damage / Crash History?</span>
              <select className={css.select} value={crash} onChange={e => setCrash(e.target.value)}>
                <option value="0">No — clean history</option>
                <option value="1">Minor tip-over / cosmetic only</option>
                <option value="2">Moderate — repaired damage</option>
                <option value="3">Major crash — disclosed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Condition */}
        <div className={css.formBlock}>
          <div className={css.formBlockTitle}>
            <span className={css.dot} /> Condition Grade
          </div>
          <div className={css.condRow}>
            {CONDITION_OPTIONS.map(opt => (
              <div
                key={opt.val}
                className={condVal === opt.val ? css.condBtnActive : css.condBtn}
                onClick={() => setCondVal(opt.val)}
              >
                <span className={css.condScore}>{opt.score}</span>
                {opt.label}
              </div>
            ))}
          </div>
        </div>

        {/* Upgrades */}
        <div className={css.formBlock}>
          <div className={css.formBlockTitle}>
            <span className={css.dot} /> Upgrades & Mods Installed
          </div>
          <div className={css.upgGrid}>
            {UPGRADES.map(u => {
              const isActive = activeUpg.has(u.id);
              return (
                <div
                  key={u.id}
                  className={isActive ? css.upgItemActive : css.upgItem}
                  onClick={() => toggleUpgrade(u.id)}
                >
                  <div className={isActive ? css.upgCbActive : css.upgCb}>&#10003;</div>
                  <span>{u.label}</span>
                  <span className={isActive ? css.upgValActive : css.upgVal}>+${u.val}</span>
                </div>
              );
            })}
          </div>
        </div>

        <button className={css.calcBtn} onClick={calculate}>
          CALCULATE RESALE VALUE
        </button>

        {/* Results */}
        {result && (
          <div className={css.results} ref={resultsRef}>
            <div className={css.resultsEyebrow}>Valuation Report</div>

            <div className={css.valCard}>
              <div className={css.valTop}>
                <div>
                  <div className={css.valEyebrow}>Estimated Private Party Value</div>
                  <div className={css.valPrice}>{fmt(result.final)}</div>
                  <div className={css.valRange}>
                    Likely range: <span className={css.valRangeSpan}>{fmt(result.low)}</span> –{' '}
                    <span className={css.valRangeSpan}>{fmt(result.high)}</span>
                  </div>
                </div>
                <div className={css.valRight}>
                  {result.badges.map((b, i) => (
                    <span key={i} className={css[BADGE_CLASS_MAP[b.cls]]}>
                      {b.text}
                    </span>
                  ))}
                </div>
              </div>
              <div className={css.confRow}>
                <div className={css.confLbl}>Confidence</div>
                <div className={css.confTrack}>
                  <div className={css.confFill} style={{ width: `${result.conf}%` }} />
                </div>
                <div className={css.confPct}>{result.conf}%</div>
              </div>
            </div>

            <div className={css.sectionLabel}>Price Breakdown</div>
            <div className={css.breakdownCard}>
              {result.rows.map((r, i) => (
                <div key={i} className={css.bkRow}>
                  <span className={css.bkLabel}>{r.label}</span>
                  <span className={`${css.bkVal} ${css[r.cls]}`}>{r.val}</span>
                </div>
              ))}
            </div>

            <div className={css.tipsCard}>
              <strong>Selling tips: </strong>
              {result.tips.join(' ')}
            </div>
          </div>
        )}

        <div className={css.disclaimer}>
          Values are estimated from observed private-party listings on Facebook Marketplace, eBay,
          Craigslist, and community forums (r/Surron, r/Talaria, Electric Cycle Rider). MSRP data
          sourced from manufacturer and authorized dealer sites as of early 2025. Upgrade values
          reflect approximate resale recovery (typically 50-65% of retail cost). This tool does not
          constitute a binding appraisal. Actual prices vary by region, demand, and listing quality.
        </div>
      </div>
    </section>
  );
};

export default SectionValueCalculator;
